import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import LeadCaptureModal from '../components/LeadCaptureModal';
import RiskHighlights, {
  RiskHighlightItem,
} from '../components/RiskHighlights';
import { RiskLevel } from '../types/risk';
import '../styles/home-landing.css';

interface JabutiFinding {
  kind?: string;
  excerpt?: string;
  interpretation?: string;
  why?: string;
}
interface JabutiPayload {
  score: number;
  level: 'low' | 'medium' | 'high';
  headline?: string;
  headline_com_autor?: string;
  summary?: string;
  summarized_title?: string;
  indicators?: string[];
  temas?: string[];
  findings?: JabutiFinding[];
}
interface JabutiItem {
  id: string;
  tipo: string;
  numero: number;
  ano: number;
  titulo: string;
  titulo_llm?: string;
  ementa: string;
  autor?: string;
  jabuti: JabutiPayload;
}
function jabutiHeadline(j: JabutiPayload): string | undefined {
  if (j.headline_com_autor) return j.headline_com_autor;
  if (j.headline) return j.headline;
  if (j.summary) {
    const first = j.summary.split(/(?<=[.!?])\s+/)[0]?.trim();
    if (first) return first.length > 160 ? first.slice(0, 157).trimEnd() + '…' : first;
  }
  return j.indicators?.[0];
}

export default function Home() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadOrigem, setLeadOrigem] = useState('landing');
  const openLead = useCallback((origem: string) => {
    setLeadOrigem(origem);
    setLeadOpen(true);
  }, []);

  const [rotatingPhrase, setRotatingPhrase] = useState('projetos sobre IA no SUS em 2025');
  const [jabutis, setJabutis] = useState<JabutiItem[]>([]);
  const [riskHighlights, setRiskHighlights] = useState<RiskHighlightItem[]>([]);

  // Fetch new IAL-based highlights. Falls back silently to empty if the
  // backend hasn't been migrated yet — the section won't render in that case.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/proposicoes/risk-analysis/top?limit=6')
      .then((r) => (r.ok ? r.json() : { results: [] }))
      .then((data) => {
        if (cancelled) return;
        const results: Array<{
          id: string;
          tipo: string;
          numero: number;
          ano: number;
          titulo: string;
          titulo_llm?: string;
          ial_score: number;
          ial_level: RiskLevel;
          risk_analysis?: { summary?: string; summarizedTitle?: string | null };
        }> = data.results || [];
        const mapped: RiskHighlightItem[] = results.map((r) => ({
          id: r.id,
          tipo: r.tipo,
          numero: r.numero,
          ano: r.ano,
          ialScore: r.ial_score,
          ialLevel: r.ial_level,
          oneLiner:
            r.risk_analysis?.summarizedTitle ||
            r.risk_analysis?.summary ||
            r.titulo_llm ||
            r.titulo,
        }));
        setRiskHighlights(mapped);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const CACHE_KEY = 'home:jabutis:top:12';
    const isLocal = import.meta.env.DEV;

    if (isLocal) {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setJabutis(parsed);
          }
        }
      } catch {}
    }

    fetch('/api/proposicoes/jabutis/top?limit=12')
      .then((r) => (r.ok ? r.json() : { results: [] }))
      .then((data) => {
        if (cancelled) return;
        const results: JabutiItem[] = data.results || [];
        if (results.length > 0) {
          setJabutis(results);
          if (isLocal) {
            try {
              localStorage.setItem(CACHE_KEY, JSON.stringify(results));
            } catch {}
          }
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const phrases = [
      'projetos sobre IA no SUS em 2025',
      'proposições que afetam motoristas de aplicativo',
      'PECs sobre reforma tributária',
      'mudanças na CLT em tramitação',
      'PLs sobre IA em tramitação na Câmara',
      'relacionado ao Marco Civil da Internet'
    ];

    let i = 0;
    const interval = setInterval(() => {
      i = (i + 1) % phrases.length;
      setRotatingPhrase(phrases[i]);
    }, 3200);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const mq = window.matchMedia('(min-width: 881px)');
    const close = () => {
      if (mq.matches) setMobileNavOpen(false);
    };
    mq.addEventListener('change', close);
    window.addEventListener('resize', close);
    return () => {
      mq.removeEventListener('change', close);
      window.removeEventListener('resize', close);
    };
  }, [mobileNavOpen]);

  return (
    <div className="landing">
      <LeadCaptureModal open={leadOpen} origem={leadOrigem} onClose={() => setLeadOpen(false)} />
      {/* ============ NAV ============ */}
      <nav className="nav">
        <div className="nav-inner">
          <a href="#" className="logo">legislacao.online</a>
          <div
            id="nav-mobile-panel"
            className={`nav-links-wrap${mobileNavOpen ? ' is-open' : ''}`}
          >
            <div className="nav-links">
              <a href="#produto" onClick={() => setMobileNavOpen(false)}>Produto</a>
              <a href="#mercado" onClick={() => setMobileNavOpen(false)}>Mercado</a>
              <a href="#confianca" onClick={() => setMobileNavOpen(false)}>Confiança</a>
              <a href="#para-quem" onClick={() => setMobileNavOpen(false)}>Para quem</a>
            </div>
          </div>
          <button
            type="button"
            className="nav-menu-toggle"
            aria-expanded={mobileNavOpen}
            aria-controls="nav-mobile-panel"
            aria-label={mobileNavOpen ? 'Fechar menu' : 'Abrir menu'}
            onClick={() => setMobileNavOpen((o) => !o)}
          >
            <span className="nav-menu-bar" aria-hidden />
            <span className="nav-menu-bar" aria-hidden />
          </button>
          <div className="nav-cta">
          </div>
        </div>
      </nav>

      {/* ============ HERO ============ */}
      <header className="hero">
        <div className="container-wide">
          <div className="hero-grid">
            <div>
              <span className="eyebrow">Análise institucional de proposições legislativas</span>
              <h1>IA para detectar alterações legislativas de alto impacto e baixa transparência.</h1>
              <p className="lede">
                legislacao.online analisa cada proposição em cinco dimensões — aderência
                temática, impacto em direitos, transparência, risco jurídico e
                complexidade regulatória — e compõe o{' '}
                <Link to="/metodologia" className="lede-link"><b>Índice de Atenção Legislativa (IAL)</b></Link>.
                Para times jurídicos, de compliance e de relações institucionais.
              </p>
              <div className="row-wrap">
                <a href="#mercado" className="btn btn-ghost btn-lg">Sobre o mercado</a>
              </div>
              <div className="hero-meta">
                <span><b>Câmara · Senado · Câmaras Municipais</b></span>
                <span><b>50+ proposições</b> detectadas como duplicatas/dia</span>
              </div>
            </div>

            {/* Preview de análises recentes */}
            <div className="ask-card" id="ask-demo">
              <div className="ask-input">
                <span className="t-mono" style={{color: 'var(--muted)'}}>⌕</span>
                <span className="ph">
                  <span className="rot-line" style={{transition: 'opacity 0.25s ease'}}>{rotatingPhrase}</span>
                  <span className="caret"></span>
                </span>
              </div>
              <div className="ask-answer">
                <div className="hint">Análises recentes · IAL · dados oficiais</div>
                <p className="resp">
                  Cada proposição é avaliada em cinco dimensões de risco com indicadores
                  textuais rastreáveis. Análise preliminar automatizada, com linguagem
                  técnica e neutra. Exemplos recentes:
                </p>
                <div className="jabuti-mini-list">
                  {jabutis.slice(0, 2).map((j) => (
                    <Link key={j.id} to={`/detail/${j.id}`} className="jabuti-mini">
                      <div className="jabuti-mini-head">
                        <b className="t-mono">{j.tipo} {j.numero}/{j.ano}</b>
                        <span className={`jabuti-pill jabuti-pill-${j.jabuti.level}`} title="Índice de Atenção Legislativa">
                          IAL {Math.round(j.jabuti.score)}
                        </span>
                      </div>
                      {jabutiHeadline(j.jabuti) && (
                        <p className="jabuti-mini-summary">{jabutiHeadline(j.jabuti)}</p>
                      )}
                    </Link>
                  ))}
                  {jabutis.length === 0 && (
                    <div className="src-item" style={{opacity: 0.55}}>
                      <span className="num">…</span>
                      <span className="title">Carregando análises recentes…</span>
                      <span className="score">IA</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ============ ANÁLISE INSTITUCIONAL (IAL) ============ */}
      {riskHighlights.length > 0 && (
        <section className="band" id="risk-analysis">
          <div className="container-wide">
            <div className="section-head">
              <span className="eyebrow">Análise institucional</span>
              <h2>
                Proposições em destaque pelo <em className="accent-em">Índice de Atenção Legislativa</em>.
              </h2>
              <p className="t-lede">
                Análise técnica multi-dimensional de proposições legislativas brasileiras.
                Cinco eixos independentes — aderência temática, impacto em direitos,
                transparência, risco jurídico e complexidade — compostos em um índice
                único. <Link to="/metodologia">Ver metodologia →</Link>
              </p>
            </div>
            <RiskHighlights
              highlights={riskHighlights}
              limit={6}
              title="Proposições com IAL elevado"
              className="home-risk-highlights"
            />
          </div>
        </section>
      )}

      {/* ============ FEATURES ============ */}
      <section className="band" id="produto">
        <div className="container-wide">
          <div className="section-head">
            <span className="eyebrow">Como funciona</span>
            <h2>Menos Congresso confuso. Mais clareza sobre o que te afeta.</h2>
            <p className="t-lede">
              Três capacidades. Uma plataforma.
            </p>
          </div>

          <div className="feat-grid">
            <div className="feat">
              <div className="num">01</div>
              <h3>Busca por significado</h3>
              <p>
                Escape de palavras-chave. Busque por conceito: "proteção de dados",
                "mobilidade urbana", "setor de saúde". O sistema entende a intenção
                e recupera proposições relevantes, não apenas correspondências textuais.
              </p>
              <div className="demo">
                <div className="t-eyebrow" style={{color: 'var(--muted)', marginBottom: '6px'}}>Exemplo</div>
                <div className="t-mono" style={{fontSize:'13px', color: 'var(--ink-2)'}}>
                  Busque "motoristas de aplicativo"<br/>
                  <span style={{color: 'var(--accent)'}}>→ encontra PLs sobre uberização, direitos trabalhistas, etc.</span>
                </div>
              </div>
            </div>

            <div className="feat">
              <div className="num">02</div>
              <h3>Navegação por temas</h3>
              <p>
                Cada proposição é mapeada em uma ou mais categorias — Saúde, Trabalho,
                Tributário, Tecnologia e 50+ temas. Navegue por interesse, compare
                atividade legislativa entre áreas.
              </p>
              <div className="demo">
                <div className="row-wrap">
                  <span className="chip chip-accent">Saúde</span>
                  <span className="chip chip-accent">Tecnologia</span>
                  <span className="chip chip-ai">Ex.: vários PL por tema</span>
                </div>
              </div>
            </div>

            <div className="feat">
              <div className="num">03</div>
              <h3>Análise de riscos legislativos</h3>
              <p>
                Cada proposição é avaliada em cinco dimensões independentes:
                aderência temática, impacto em direitos, transparência, risco
                jurídico e complexidade regulatória. Indicadores rastreáveis,
                trechos citados literalmente, sem afirmações absolutas.
                <Link to="/metodologia" className="lede-link">Ver metodologia →</Link>
              </p>
              <div className="demo">
                <div className="t-eyebrow" style={{color: 'var(--muted)', marginBottom: '8px'}}>Exemplo real recente</div>
                {jabutis[0] ? (
                  <Link to={`/detail/${jabutis[0].id}`} className="src-item jabuti-row" style={{background: 'var(--paper)'}}>
                    <span className="num">!</span>
                    <span className="title">
                      <b>{jabutis[0].tipo} {jabutis[0].numero}/{jabutis[0].ano}</b>
                      {jabutiHeadline(jabutis[0].jabuti) || jabutis[0].jabuti.summarized_title || jabutis[0].titulo_llm || jabutis[0].titulo}
                    </span>
                    <span className={`jabuti-pill jabuti-pill-${jabutis[0].jabuti.level}`} title="Índice de Atenção Legislativa">
                      IAL {Math.round(jabutis[0].jabuti.score)}
                    </span>
                  </Link>
                ) : (
                  <div className="src-item" style={{background: 'var(--paper)'}}>
                    <span className="num">!</span>
                    <span className="title"><b>Carregando…</b> análise recente</span>
                    <span className="score">IAL</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PIPELINE ============ */}
      <section className="band band-dark" id="funciona">
        <div className="container-wide">
          <div className="section-head">
            <span className="eyebrow">Como funciona</span>
            <h2 style={{color: 'var(--paper)'}}>Busca inteligente com <em className="accent-em" style={{color: 'var(--accent-soft)'}}>respostas</em> verificáveis.</h2>
            <p className="t-lede">
              Indexamos as proposições do Congresso e usamos IA para entender o que você pergunta.
              Mas nunca inventamos: toda resposta aponta direto para o texto oficial.
            </p>
          </div>

          <div className="pipeline">
            <div className="step">
              <span className="tag det">Confiável</span>
              <div className="step-num">01</div>
              <h4>Dados reais</h4>
              <p>Obtemos todas as proposições direto das APIs oficiais da Câmara e Senado. Sempre atualizados.</p>
            </div>
            <div className="step">
              <span className="tag det">Rápido</span>
              <div className="step-num">02</div>
              <h4>Busca semântica</h4>
              <p>Pergunte em português corrente. O sistema entende a intenção, não apenas palavras-chave.</p>
            </div>
            <div className="step">
              <span className="tag ai">Inteligente</span>
              <div className="step-num">03</div>
              <h4>Classificação automática</h4>
              <p>Cada PL é automaticamente categorizado por tema — Saúde, Trabalho, Tecnologia e mais de 50 categorias.</p>
            </div>
            <div className="step">
              <span className="tag ai">Citado</span>
              <div className="step-num">04</div>
              <h4>Com fontes</h4>
              <p>Toda resposta aponta o trecho oficial que a justifica. Clique para ler o texto completo.</p>
            </div>
            <div className="step">
              <span className="tag det">Transparente</span>
              <div className="step-num">05</div>
              <h4>Sem mistério</h4>
              <p>Confiança visível em cada classificação e busca. O sistema mostra quando tem dúvida.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRUST ============ */}
      <section className="band" id="confianca">
        <div className="container-wide">
          <div className="section-head">
            <span className="eyebrow">Confiança</span>
            <h2>A IA é índice e tradutor — nunca a fonte da verdade.</h2>
            <p className="t-lede">
              Modelos de linguagem são bons em descobrir e em traduzir. Não confiamos
              neles para afirmar fatos. O design da legislacao.online torna essa fronteira visível.
            </p>
          </div>

          <div className="trust-layout">
            <div className="stack-lg">
              <div>
                <div className="t-mono" style={{color: 'var(--accent)', fontSize: '12px'}}>Regra 1</div>
                <h3 className="t-h3" style={{margin: '6px 0 8px'}}>Toda afirmação tem citação clicável</h3>
                <p className="t-body" style={{color: 'var(--ink-2)', margin: '0'}}>
                  O modelo é proibido de responder sem ancorar em trechos recuperados.
                  Cada citação abre o texto oficial no contexto exato.
                </p>
              </div>
              <div>
                <div className="t-mono" style={{color: 'var(--accent)', fontSize: '12px'}}>Regra 2</div>
                <h3 className="t-h3" style={{margin: '6px 0 8px'}}>Dados estruturados não passam por IA</h3>
                <p className="t-body" style={{color: 'var(--ink-2)', margin: '0'}}>
                  Tipo, número, autor, datas, status, casa, tramitação — vêm direto da API.
                  Sem reescrita, sem resumo, sem inferência.
                </p>
              </div>
              <div>
                <div className="t-mono" style={{color: 'var(--accent)', fontSize: '12px'}}>Regra 3</div>
                <h3 className="t-h3" style={{margin: '6px 0 8px'}}>Confiança visível</h3>
                <p className="t-body" style={{color: 'var(--ink-2)', margin: '0'}}>
                  Score de classificação e similaridade aparece no card. Abaixo de um threshold,
                  o sistema mostra "revisão pendente" em vez de adivinhar.
                </p>
              </div>
            </div>

            {/* Trust card visual */}
            <div className="card card-lg">
              <div className="row" style={{marginBottom: 'var(--s-4)', justifyContent: 'space-between'}}>
                <div className="row" style={{gap: '8px'}}>
                  <span className="type-tag pl">PL</span>
                  <strong className="t-mono" style={{fontSize:'14px'}}>2338/2023</strong>
                </div>
                <span className="status live">Em tramitação</span>
              </div>
              <p style={{margin: '0 0 var(--s-4)', fontSize: '15px', lineHeight: '1.55'}}>
                Ementa oficial (Senado): dispõe sobre o uso da inteligência artificial.
                Texto completo e atualizações de tramitação vêm da API da casa.
              </p>
              <div style={{display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px var(--s-3)', padding: 'var(--s-3) 0', borderTop: '1px solid var(--rule)', borderBottom: '1px solid var(--rule)', fontSize: '13px'}}>
                <span className="t-eyebrow" style={{color: 'var(--muted)'}}>Autor</span>
                <span>Sen. Rodrigo Pacheco · PSD-MG</span>
                <span className="t-eyebrow" style={{color: 'var(--muted)'}}>Apresentado</span>
                <span className="t-mono" style={{fontSize:'12px'}}>03/05/2023</span>
                <span className="t-eyebrow" style={{color: 'var(--muted)'}}>Casa</span>
                <span>Senado Federal</span>
              </div>
              <div style={{marginTop: 'var(--s-3)', fontSize: '11.5px', color: 'var(--muted)', display: 'flex', gap: '4px', alignItems: 'center'}}>
                <span style={{display:'inline-block', width:'5px', height:'5px', borderRadius: '50%', background: 'var(--muted)'}}></span>
                Dados oficiais — Senado Federal · API
              </div>

              <div style={{marginTop: 'var(--s-4)', paddingTop: 'var(--s-4)', borderTop: '1px dashed var(--rule-2)'}}>
                <div className="t-eyebrow" style={{color: 'var(--ai)', marginBottom: '8px'}}>Temas · exemplo ilustrativo (scores fictícios)</div>
                <div className="row-wrap">
                  <span className="chip chip-ai">Tecnologia · 0.94</span>
                  <span className="chip chip-ai">Direitos digitais · 0.88</span>
                  <span className="chip" style={{background: 'transparent', borderStyle: 'dashed'}}>+ por quê</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ MERCADO ============ */}
      <section className="band" id="mercado" style={{background: 'var(--paper-2)'}}>
        <div className="container-wide">
          <div className="section-head">
            <span className="eyebrow">Posição de mercado</span>
            <h2>Por que legislacao.online venceu o mercado legislativo brasileiro.</h2>
            <p className="t-lede">
              O Brasil tinha 5 soluções principais. Todas focadas em B2B caro. Nós entramos pelo cidadão.
            </p>
          </div>

          <div className="compare">
            <div className="compare-row head">
              <div>Competidor</div>
              <div>Estratégia</div>
              <div>Por que ganhamos</div>
            </div>

            <div className="compare-row">
              <div className="label">Inteligov</div>
              <div className="market">Pioneira. IA + termômetro + matriz de poder. Foco: RelGov corporativo. Preço: R$ 50k+/ano.</div>
              <div className="ours">✅ Nós oferecemos IA + detecção + gratuito. Conquistamos jornalistas e ONGs que não pagam.</div>
            </div>

            <div className="compare-row">
              <div className="label">DataPolicy</div>
              <div className="market">SaaS para monitoramento diário de mudanças. Foco: Relações Governamentais. Caro ($$$).</div>
              <div className="ours">✅ Oferecemos descoberta + monitoramento + descoberta automática de similares.</div>
            </div>

            <div className="compare-row">
              <div className="label">Radar Governamental</div>
              <div className="market">Cobertura ampla (3 poderes, estados, 100+ municípios) + equipe humana. Caro, manual.</div>
              <div className="ours">✅ Automação 10x mais rápida, 10x mais barata. Mesma cobertura, zero custo.</div>
            </div>

            <div className="compare-row">
              <div className="label">LeisMunicipais</div>
              <div className="market">1.800 municípios. Freemium. Foco: transparência municipal. UX antiga.</div>
              <div className="ours">✅ Design moderno + IA nativa. </div>
            </div>

            <div className="compare-row">
              <div className="label">Senado.leg.br / Câmara.leg.br</div>
              <div className="market">Oficial, completo, 38k proposições. Mas: interface de 2005, sem busca inteligente.</div>
              <div className="ours">✅ Mesmos dados, design atualizado, busca semântica, detecção automática de duplicatas.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ COMPARISON ============ */}
      <section className="band">
        <div className="container-wide">
          <div className="section-head">
            <span className="eyebrow">Diferencial Técnico</span>
            <h2>O mercado tem ERPs de relações governamentais. Falta um <em className="accent-em">copilot</em> de pesquisa.</h2>
          </div>

          <div className="compare">
            <div className="compare-row head">
              <div>Capacidade</div>
              <div>Mercado atual</div>
              <div>legislacao.online</div>
            </div>
            <div className="compare-row">
              <div className="label">Busca</div>
              <div className="market">Palavra-chave. Você precisa adivinhar termos.</div>
              <div className="ours">Linguagem natural. Pergunte como falaria a um colega.</div>
            </div>
            <div className="compare-row">
              <div className="label">Classificação temática</div>
              <div className="market">Configurada manualmente, por keyword, por usuário.</div>
              <div className="ours">Automática para toda proposição, taxonomia fechada.</div>
            </div>
            <div className="compare-row">
              <div className="label">Similaridade</div>
              <div className="market">Não existe. Listas paralelas, sem cruzamento.</div>
              <div className="ours">Grafo entre proposições. Detecta PLs concorrentes automaticamente.</div>
            </div>
            <div className="compare-row">
              <div className="label">IA</div>
              <div className="market">Caixa-preta — "curadoria automática".</div>
              <div className="ours">Citação obrigatória. Confiança exposta.</div>
            </div>
            <div className="compare-row">
              <div className="label">Postura</div>
              <div className="market">ERP de relações governamentais — workflow de time.</div>
              <div className="ours">Copilot de pesquisa — descoberta e investigação.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOR WHO ============ */}
      <section className="band" id="para-quem">
        <div className="container-wide">
          <div className="section-head">
            <span className="eyebrow">Para quem</span>
            <h2>legislacao.online é feito para qualquer um que precise entender o que o Congresso aprova.</h2>
          </div>

          <div className="who-grid">
            <div className="who">
              <div className="role">01 · Jornalistas</div>
              <h4>Encontre histórias que ninguém viu</h4>
              <p>Busque por conceito: "IA no SUS", "direitos de motoristas de app", "reforma tributária". Descubra PLs relacionadas que nenhum concorrente mencionou. Detecte quando o mesmo projeto é votado com nomes diferentes.</p>
            </div>
            <div className="who">
              <div className="role">02 · Ativistas / ONGs</div>
              <h4>Monitore pautas que te importam</h4>
              <p>Crie watchlists por tema (direitos humanos, meio ambiente, trabalho). Receba alertas quando proposições relevantes avançam. Saiba quando PLs similares estão em tramitação simultânea.</p>
            </div>
            <div className="who">
              <div className="role">03 · Advogados / Consultórios</div>
              <h4>Acompanhe impacto regulatório</h4>
              <p>Monitore proposições que afetam seus clientes. Entenda contexto legislativo. Ofereça inteligência legislativa como valor agregado.</p>
            </div>
            <div className="who">
              <div className="role">04 · Cidadãos</div>
              <h4>Fique informado sobre decisões que te afetam</h4>
              <p>Não precisa de jargão legislativo. Busque em linguagem natural. Entenda o que está sendo votado e por quê.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="cta" id="cta">
        <span className="eyebrow" style={{color: 'var(--accent-soft)'}}>Acesso à plataforma</span>
        <h2 style={{marginTop: 'var(--s-4)'}}>Entenda o que está acontecendo no Congresso.</h2>
        <p className="lede t-lede">
          Solicite acesso: respondemos com o próximo passo. Sem cartão, sem complicação.
        </p>
        <div className="row-wrap">
          <button type="button" className="btn btn-primary btn-lg" onClick={() => openLead('cta')}>
            Solicitar acesso
          </button>
          <a href="#mercado" className="btn btn-ghost btn-lg">Voltar ao topo</a>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="foot">
        <div className="container-wide">
          <div className="foot-grid">
            <div>
              <div className="logo on-dark">legislacao.online</div>
              <p style={{marginTop: '12px', maxWidth: '32ch', fontSize: '13px', lineHeight: '1.5'}}>
                A ferramenta padrão para legislação no Brasil.
              </p>
            </div>
            <div className="foot-col">
              <h5>Produto</h5>
              <ul>
                <li><a href="#produto">Busca semântica</a></li>
                <li><a href="#produto">Detectar similares</a></li>
                <li><a href="#produto">Watchlists</a></li>
                <li><a href="#">API pública</a></li>
              </ul>
            </div>
            <div className="foot-col">
              <h5>Transparência</h5>
              <ul>
                <li><a href="#confianca">Como funciona IA</a></li>
                <li><a href="#">Política de dados</a></li>
                <li><a href="#">Open source</a></li>
                <li><a href="#">Segurança</a></li>
              </ul>
            </div>
            <div className="foot-col">
              <h5>Empresa</h5>
              <ul>
                <li><a href="#">Sobre nós</a></li>
                <li><a href="#">Blog</a></li>
                <li><a href="#">Imprensa</a></li>
                <li>
                  <button type="button" className="foot-link-btn" onClick={() => openLead('footer_contato')}>
                    Contato
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="foot-bottom">
            <span>© 2026 legislacao.online</span>
            <span>Dados de fontes oficiais</span>
            <span style={{fontSize: '12px', color: 'rgba(244,241,234,0.5)'}}>
              Feito com ❤️ para democracia mais transparente
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
